import { Fragment, useState } from "react";
import {
  ActionType,
  ClientIntegration,
  IntegrationType,
  useTriggerActionLazyQuery,
} from "~/operations";
import { IntegrationRecommendedPolicies } from "../../../components/IntegrationRecommendedPolicies";
import { createPath, parsePath } from "react-router-dom";
import { IntegrationAddHeader } from "../../../headers/IntegrationAddHeader";
import { UpdateFlowData } from "../../../utils/integrationsTypes";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { TabPanel } from "~/components/tab-panel";
import { useCanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { useCanUpdate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Update";
import useGenerateIntegrationName from "../../../utils/integrationsUtils";
import { Ms365ManualForm } from "~/pages/integrations/hosted-integrations/forms/ms365/components/Ms365ManualForm";
import { Ms365AutomatedForm } from "~/pages/integrations/hosted-integrations/forms/ms365/components/Ms365AutomatedForm";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

export function Ms365IntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  const [createdIntegrationMrn, setCreatedIntegrationMrn] = useState<
    ClientIntegration["mrn"] | undefined
  >(undefined);
  const [view, setView] = useState<"setup" | "add policies">("setup");
  const nextDefaultIntegrationName = useGenerateIntegrationName({ scope });
  const [defaultIntegrationName, _setDefaultIntegrationName] = useState<string>(
    () => nextDefaultIntegrationName || "new-ms365-integration",
  );

  const canCreateIntegration = useCanCreate();
  const canUpdateIntegration = useCanUpdate();

  const hasAutomatedDeploymentPermissions = updateFlow
    ? canUpdateIntegration
    : canCreateIntegration;

  const [TriggerClientIntegrationScan] = useTriggerActionLazyQuery({
    variables: {
      input: { mrn: createdIntegrationMrn!, type: ActionType.RunScan },
    },
  });

  const handleTriggerScan = async () => {
    try {
      await TriggerClientIntegrationScan();
    } catch (e) {
      console.error(e);
    }
  };

  const integrationPath = `/space/integrations/add/mondoo/ms365?${scope.params}`;

  const generateHref = (tab: string): string => {
    const parsedPath = parsePath(integrationPath);
    const pathname = `${parsedPath.pathname}/${tab}`;
    const search = parsedPath.search;
    return createPath({ pathname, search });
  };

  const tabList: TabListItem[] = [
    {
      label: "Automated setup",
      to: generateHref("automated"),
      route: "/automated",
    },
    {
      label: "Manual setup",
      to: generateHref("manual"),
      route: "/manual",
    },
  ];

  // if updateFlow exists, we should default to the manual tab
  const initialTab = updateFlow ? "manual" : "automated";

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    initialTab,
  );

  return (
    <Fragment>
      {view === "setup" ? (
        <Fragment>
          <IntegrationAddHeader {...{ type: IntegrationType.Ms365 }} />
          <Fragment>
            <TabNavigation
              id={`${IntegrationType.Ms365}-tabs`}
              tabList={tabList}
              currentTab={currentTab}
            />
            <TabPanel value={currentTab} index="/automated">
              <Ms365AutomatedForm
                {...{
                  hasAutomatedDeploymentPermissions,
                  scope,
                  defaultIntegrationName,
                  setView,
                }}
              />
            </TabPanel>
            <TabPanel value={currentTab} index="/manual">
              <Ms365ManualForm
                {...{
                  defaultIntegrationName,
                  scope,
                  setView,
                  updateFlow,
                  setCreatedIntegrationMrn,
                }}
              />
            </TabPanel>
          </Fragment>
        </Fragment>
      ) : (
        <IntegrationRecommendedPolicies
          {...{ scope }}
          filterTypes={["microsoft365"]}
          finalizeAction={handleTriggerScan}
        />
      )}
    </Fragment>
  );
}
